import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AppData, UnifiedAppMetadata, AppleStoreApp, GooglePlayApp, StoreType } from '../types';
import {
  resolveAppIcon,
  getLastKnownGoodIcon,
  saveLastKnownGoodIcon,
  invalidateBadIconUrl,
  isRemoteArtworkUrl,
  VERIFIED_LOADED_URLS,
  KNOWN_APPLE_ARTWORKS
} from '../services/iconCache';

export { KNOWN_APPLE_ARTWORKS, resolveAppIcon, getLastKnownGoodIcon, saveLastKnownGoodIcon };

export interface AppIconProps {
  app?: Partial<AppData | UnifiedAppMetadata | AppleStoreApp | GooglePlayApp | any> | null;
  src?: string | null;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | number | string;
  shape?: 'squircle' | 'circle' | 'rounded';
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';
  className?: string;
  alt?: string;
  store?: StoreType;
  showStoreBadge?: boolean;
}

// Brand Style configuration for deterministic monogram fallback underneath
export interface BrandStyle {
  gradient: string;
  textColor: string;
  initial?: string;
}

const BRAND_PALETTES: Record<string, BrandStyle> = {
  duolingo: { gradient: 'from-[#58CC02] to-[#2E8B00]', textColor: 'text-white', initial: 'D' },
  chatgpt: { gradient: 'from-[#10A37F] to-[#0A6B53]', textColor: 'text-white', initial: 'C' },
  spotify: { gradient: 'from-[#1ED760] to-[#128C3E]', textColor: 'text-white', initial: 'S' },
  tiktok: { gradient: 'from-[#010101] via-[#111827] to-[#FE2C55]', textColor: 'text-white', initial: 'T' },
  canva: { gradient: 'from-[#00C4CC] to-[#7D2AE8]', textColor: 'text-white', initial: 'C' },
  capcut: { gradient: 'from-[#18181B] via-[#0F172A] to-[#2563EB]', textColor: 'text-white', initial: 'C' },
  instagram: { gradient: 'from-[#833AB4] via-[#FD1D1D] to-[#FCB045]', textColor: 'text-white', initial: 'I' },
  notion: { gradient: 'from-[#2F3437] to-[#111827]', textColor: 'text-white', initial: 'N' },
  babbel: { gradient: 'from-[#FF6400] to-[#D64000]', textColor: 'text-white', initial: 'B' },
  busuu: { gradient: 'from-[#0070BA] to-[#004B87]', textColor: 'text-white', initial: 'B' },
  memrise: { gradient: 'from-[#FFE600] to-[#DCA100]', textColor: 'text-slate-950 font-black', initial: 'M' },
  'rosetta stone': { gradient: 'from-[#FFB81C] to-[#002D62]', textColor: 'text-white', initial: 'R' }
};

const GENERAL_GRADIENTS = [
  'from-blue-600 to-indigo-700',
  'from-emerald-500 to-teal-700',
  'from-violet-600 to-purple-800',
  'from-amber-500 to-orange-700',
  'from-rose-500 to-pink-700',
  'from-sky-500 to-blue-700',
  'from-cyan-600 to-blue-800',
  'from-fuchsia-600 to-rose-700',
  'from-indigo-500 to-violet-800',
  'from-teal-500 to-emerald-800'
];

export function getBrandStyleForApp(appName: string): BrandStyle {
  const clean = (appName || 'App').trim().toLowerCase();
  for (const [key, val] of Object.entries(BRAND_PALETTES)) {
    if (clean.includes(key)) {
      return val;
    }
  }

  let hash = 0;
  for (let i = 0; i < clean.length; i++) {
    hash = clean.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % GENERAL_GRADIENTS.length;
  return {
    gradient: GENERAL_GRADIENTS[index],
    textColor: 'text-white',
    initial: (appName.trim().charAt(0) || 'A').toUpperCase()
  };
}

const SIZE_PRESETS: Record<string, { sizePx: number; defaultClasses: string; textClass: string }> = {
  xs: { sizePx: 24, defaultClasses: 'w-6 h-6', textClass: 'text-[11px] font-extrabold' },
  sm: { sizePx: 32, defaultClasses: 'w-8 h-8', textClass: 'text-xs font-black' },
  md: { sizePx: 44, defaultClasses: 'w-11 h-11', textClass: 'text-base font-black' },
  lg: { sizePx: 48, defaultClasses: 'w-12 h-12', textClass: 'text-lg font-black' },
  xl: { sizePx: 64, defaultClasses: 'w-16 h-16', textClass: 'text-2xl font-black' },
  '2xl': { sizePx: 80, defaultClasses: 'w-20 h-20', textClass: 'text-3xl font-black' }
};

const ROUNDED_MAP: Record<string, string> = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
  '3xl': 'rounded-3xl',
  full: 'rounded-full'
};

export const AppIcon: React.FC<AppIconProps> = ({
  app,
  src,
  name,
  size = 'md',
  shape,
  rounded = '2xl',
  className = '',
  alt,
  store,
  showStoreBadge = false
}) => {
  const appName = name || app?.name || 'App';
  const effectiveStore = store || (app && 'primaryStore' in app ? app.primaryStore : undefined);
  const appStoreKey = app?.appStoreId
    ? String(app.appStoreId)
    : app?.slug || app?.id
    ? String(app.slug || app.id)
    : '';

  // Stable candidate artwork URL resolution
  const candidateUrl: string | null = useMemo(() => {
    if (src && isRemoteArtworkUrl(src)) {
      return src.trim();
    }
    if (app) {
      const resolved = resolveAppIcon(app);
      if (resolved && isRemoteArtworkUrl(resolved)) {
        return resolved;
      }
    }
    if (name) {
      const clean = name.toLowerCase();
      for (const [k, artwork] of Object.entries(KNOWN_APPLE_ARTWORKS)) {
        if (clean.includes(k) && isRemoteArtworkUrl(artwork)) {
          return artwork;
        }
      }
    }
    return null;
  }, [src, app?.appStoreId, app?.slug, app?.id, app?.iconUrl, app?.artworkUrl512, app?.artworkUrl100, appName]);

  // Keep track of active URL and load states
  const [activeUrl, setActiveUrl] = useState<string | null>(candidateUrl);
  const [isLoaded, setIsLoaded] = useState<boolean>(() => {
    return candidateUrl ? VERIFIED_LOADED_URLS.has(candidateUrl) : false;
  });
  const [hasFailed, setHasFailed] = useState<boolean>(!candidateUrl);

  const activeUrlRef = useRef(activeUrl);
  activeUrlRef.current = activeUrl;

  // Synchronize when candidateUrl changes without resetting unnecessarily
  useEffect(() => {
    if (candidateUrl) {
      setActiveUrl(candidateUrl);
      setHasFailed(false);
      if (VERIFIED_LOADED_URLS.has(candidateUrl)) {
        setIsLoaded(true);
      }
    } else {
      setActiveUrl(null);
      setHasFailed(true);
      setIsLoaded(false);
    }
  }, [candidateUrl]);

  // Debug logging for auditing regressions
  useEffect(() => {
    if (app && app.name) {
      console.log('APP ICON DEBUG', {
        name: app.name,
        appStoreId: app.appStoreId,
        iconUrl: app.iconUrl,
        artwork512: (app as any).artworkUrl512,
        cached: appStoreKey ? getLastKnownGoodIcon(appStoreKey) : null,
        resolved: candidateUrl,
        loaded: isLoaded,
        failed: hasFailed
      });
    }
  }, [app?.name, candidateUrl, isLoaded, hasFailed]);

  // Image onLoad verification: naturalWidth > 0 is mandatory
  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      VERIFIED_LOADED_URLS.add(img.src);
      setIsLoaded(true);
      setHasFailed(false);

      if (appStoreKey && isRemoteArtworkUrl(img.src)) {
        saveLastKnownGoodIcon(appStoreKey, img.src, img.naturalWidth, img.naturalHeight);
      }
    } else {
      handleImageError(event);
    }
  };

  // Image onError fallback handling
  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const failedSrc = event.currentTarget.src || activeUrl || '';
    console.warn('[AppIcon] Artwork load failed for:', appName, failedSrc);

    if (appStoreKey && failedSrc) {
      invalidateBadIconUrl(appStoreKey, failedSrc);
    }

    // Try background Apple metadata lookup if appStoreId exists and we haven't tried yet
    if (app?.appStoreId && typeof window !== 'undefined') {
      fetch(`/api/apple/lookup?id=${app.appStoreId}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((json) => {
          if (json && json.results && json.results[0]) {
            const itm = json.results[0];
            const freshUrl = itm.artworkUrl512 || itm.artworkUrl100;
            if (freshUrl && isRemoteArtworkUrl(freshUrl)) {
              setActiveUrl(freshUrl);
              setHasFailed(false);
              return;
            }
          }
          setHasFailed(true);
          setIsLoaded(false);
        })
        .catch(() => {
          setHasFailed(true);
          setIsLoaded(false);
        });
    } else {
      setHasFailed(true);
      setIsLoaded(false);
    }
  };

  const brandStyle = getBrandStyleForApp(appName);

  // Determine rounded class based on shape or rounded prop
  let roundedClass = ROUNDED_MAP[rounded] || 'rounded-2xl';
  if (shape === 'circle') {
    roundedClass = 'rounded-full';
  } else if (shape === 'squircle') {
    roundedClass = 'rounded-2xl';
  }

  // Dimension & typography styling
  let presetClasses = '';
  let textClass = 'text-sm font-black';
  let customStyle: React.CSSProperties = {};

  if (typeof size === 'string') {
    if (SIZE_PRESETS[size]) {
      presetClasses = SIZE_PRESETS[size].defaultClasses;
      textClass = SIZE_PRESETS[size].textClass;
    }
  } else if (typeof size === 'number') {
    customStyle = {
      width: `${size}px`,
      height: `${size}px`,
      minWidth: `${size}px`,
      minHeight: `${size}px`
    };
    if (size <= 28) textClass = 'text-[11px] font-extrabold';
    else if (size <= 36) textClass = 'text-xs font-black';
    else if (size <= 52) textClass = 'text-base font-black';
    else textClass = 'text-2xl font-black';
  }

  return (
    <div
      className={`relative inline-flex items-center justify-center flex-shrink-0 select-none overflow-hidden ${roundedClass} ${presetClasses} ${className}`}
      style={customStyle}
    >
      {/* 1. Base Layer (z-index: 1): Fallback Monogram (Shown ONLY during initial network load or if failed) */}
      <div
        style={{ zIndex: 1 }}
        className={`absolute inset-0 w-full h-full flex items-center justify-center bg-gradient-to-br ${brandStyle.gradient} ${brandStyle.textColor} shadow-inner border border-white/20 select-none ${roundedClass}`}
        title={appName}
      >
        <span className={`${textClass} leading-none tracking-tight`}>
          {brandStyle.initial || appName.charAt(0).toUpperCase()}
        </span>
      </div>

      {/* 2. Top Layer (z-index: 2): Real High-Res Official App Store Artwork */}
      {!hasFailed && activeUrl && (
        <img
          src={activeUrl}
          alt={alt || `${appName} icon`}
          loading="eager"
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 2
          }}
          className={`transition-opacity duration-150 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          } ${roundedClass}`}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      )}

      {/* Optional store badge on corner (z-index: 3) */}
      {showStoreBadge && effectiveStore && effectiveStore !== 'all' && (
        <div
          style={{ zIndex: 3 }}
          className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-white shadow-xs border border-slate-200 flex items-center justify-center p-0.5"
          title={effectiveStore === 'appstore' ? 'App Store' : 'Google Play'}
        >
          {effectiveStore === 'appstore' ? (
            <svg className="w-2.5 h-2.5 text-blue-600 fill-current" viewBox="0 0 24 24">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.64-.78 1.08-1.86.96-2.95-1 .04-2.12.67-2.78 1.45-.58.68-1.1 1.77-.96 2.83 1.12.09 2.14-.55 2.78-1.33z" />
            </svg>
          ) : (
            <svg className="w-2.5 h-2.5 text-emerald-600 fill-current" viewBox="0 0 24 24">
              <path d="M3 20.5v-17c0-.83.94-1.3 1.6-.8l14 8.5c.67.4.67 1.4 0 1.8l-14 8.5c-.66.5-1.6.03-1.6-.8z" />
            </svg>
          )}
        </div>
      )}
    </div>
  );
};

export default AppIcon;
